import { useRef, useState, useCallback } from "react";
import { toPng } from "html-to-image";
import { useGetFeaturedTenders } from "@workspace/api-client-react";
import { useAuth } from "@clerk/react";
import AdminLayout from "./AdminLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Download, Send, Calendar, CheckCircle2, AlertCircle, Loader2, Image as ImageIcon, Sparkles, Upload } from "lucide-react";

// ── Constants ────────────────────────────────────────────────────────────────

const WEEKDAYS = ["星期一", "星期二", "星期三", "星期四", "星期五", "自訂"];
const POST_HOUR = 17;
const POST_MINUTE = 49;

interface CatInfo { emoji: string; label: string; hashtags: string[] }
const CATEGORY_INFO: Record<string, CatInfo> = {
  "catering":       { emoji: "🍱", label: "餐飲",   hashtags: ["#飲食業", "#學校飯堂", "#餐飲服務商", "#食品供應"] },
  "engineering":    { emoji: "🔧", label: "工程",   hashtags: ["#工程業", "#建築工程", "#維修工程", "#地盤工程"] },
  "it":             { emoji: "💻", label: "IT",     hashtags: ["#IT業", "#科技", "#電腦系統", "#資訊科技"] },
  "cleaning":       { emoji: "🧹", label: "清潔",   hashtags: ["#清潔業", "#清潔服務", "#校園清潔"] },
  "security":       { emoji: "🛡️", label: "保安",   hashtags: ["#保安業", "#保安服務", "#校園保安"] },
  "air-conditioning":{ emoji: "❄️", label: "冷氣",  hashtags: ["#冷氣業", "#空調工程", "#冷氣維修"] },
  "printing":       { emoji: "🖨️", label: "印刷",   hashtags: ["#印刷業", "#書本印刷"] },
  "renovation":     { emoji: "🏗️", label: "裝修",   hashtags: ["#裝修業", "#學校裝修"] },
  "excursion":      { emoji: "✈️", label: "遊學",   hashtags: ["#遊學", "#境外考察", "#遊學機構", "#旅行社招標"] },
  "furniture":      { emoji: "🪑", label: "傢俬",   hashtags: ["#傢俬業", "#學校傢俬"] },
  "education":      { emoji: "📚", label: "教育",   hashtags: ["#教育業", "#教學服務"] },
  "others":         { emoji: "📋", label: "其他",   hashtags: ["#其他服務", "#學校服務"] },
};

function catInfo(slug: string): CatInfo {
  return CATEGORY_INFO[slug] ?? { emoji: "📋", label: "其他", hashtags: ["#其他服務"] };
}

// ── Caption generator ────────────────────────────────────────────────────────

function daysUntil(dateStr: string): number {
  const d = new Date(dateStr);
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.ceil((d.getTime() - t.getTime()) / 86400000);
}

function generateCaption(tender: any, idx: number): string {
  const cat = tender.categories?.[0];
  const info = cat ? catInfo(cat.slug) : catInfo("others");
  const catLabel = tender.categories?.map((c: any) => c.nameChinese).join(" / ") ?? "其他";
  const days = daysUntil(tender.deadlineDate);
  const daysStr = days > 0 ? `（僅剩 ${days} 日！）` : "（已接近截止！）";

  const pitches = [
    "每個學校採購都是商機！Skoltend 幫你第一時間鎖定目標，精準投標。",
    "全港學校招標一手掌握。Skoltend 讓你比競爭對手更快行動！",
    "新學年採購季開始！Skoltend 追蹤全港 1,173 所學校，助你搶佔先機。",
    "業界人士必備工具！Skoltend 每日更新，確保你不錯過任何學校標案。",
    "Skoltend 為你實時追蹤全港最新學校招標資訊，協助你第一時間鎖定商機，精準投標。",
  ];
  const pitch = pitches[idx % pitches.length];

  const baseHashtags = ["#Skoltend", "#學校招標", "#書面報價", "#香港學校招標網", "#香港中小企", "#B2B商機", "#旅遊業商機", "#商業情報", "#創業香港"];
  const districtTag = tender.district ? `#${tender.district.replace("區", "")}` : "";
  const allHashtags = [
    ...info.hashtags,
    districtTag,
    ...baseHashtags,
  ].filter(Boolean).join(" ");

  return `${info.emoji}【${info.label}標案情報】${tender.district ?? "香港"} — ${tender.title}招標中！

📍 地區：${tender.district ?? "香港"}

🏷️ 類別：${catLabel}

⏳ 截止日期：${tender.deadlineDate}${daysStr}

${pitch}

👉 點擊 skoltend.com 查看完整招標文件與校方聯絡細節！

${allHashtags}`;
}

// ── Schedule timestamp ────────────────────────────────────────────────────────

// dayIndex 0=Mon…4=Fri (fixed); dayIndex 5 uses the customIso datetime string
function getScheduleTimestamp(dayIndex: number, customIso?: string): number {
  if (dayIndex === 5 && customIso) return Math.floor(new Date(customIso).getTime() / 1000);
  const now = new Date();
  const targetDayOfWeek = dayIndex + 1;
  let daysToAdd = (targetDayOfWeek - now.getDay() + 7) % 7;
  if (daysToAdd === 0) {
    const curMins = now.getHours() * 60 + now.getMinutes();
    if (curMins >= POST_HOUR * 60 + POST_MINUTE) daysToAdd = 7;
  }
  const target = new Date(now);
  target.setDate(now.getDate() + daysToAdd);
  target.setHours(POST_HOUR, POST_MINUTE, 0, 0);
  return Math.floor(target.getTime() / 1000);
}

function formatScheduleDate(dayIndex: number, customIso?: string): string {
  const ts = getScheduleTimestamp(dayIndex, customIso) * 1000;
  return new Date(ts).toLocaleString("zh-HK", { month: "numeric", day: "numeric", weekday: "short", hour: "2-digit", minute: "2-digit" });
}

// ── WeeklyExample mobile screenshot (390×390 square capture) ────────────────

function WeeklyExampleMobileCapture({ tender, weekKey, idx }: {
  tender: any;
  weekKey: string;
  idx: number;
}) {
  const days = daysUntil(tender.deadlineDate);
  const catLabel = tender.categories?.map((c: any) => c.nameChinese).join(" / ") ?? "其他";
  const weekNum  = weekKey ? parseInt(weekKey.split("-W")[1]) : 0;
  const weekYear = weekKey ? weekKey.split("-W")[0] : "";
  const weekLabel = weekKey ? `${weekYear} 年第 ${weekNum} 週` : "";
  const isOpen = tender.status === "open";
  const ff = "'PingFang TC','Noto Sans TC','Microsoft JhengHei','Helvetica Neue',system-ui,sans-serif";

  return (
    <div style={{ width: 390, fontFamily: ff, background: "#f8fafc", display: "flex", flexDirection: "column" }}>

      {/* ── Header ── */}
      <div style={{
        background: "linear-gradient(135deg, #0a3520 0%, #15803d 60%, #166534 100%)",
        padding: "16px 16px 14px", textAlign: "center", color: "white",
      }}>
        {/* 每週更新 badge */}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)",
          borderRadius: 20, padding: "4px 13px",
          fontSize: 13, fontWeight: 600, color: "#fafa50", marginBottom: 10,
        }}>🔄 每週更新</div>

        {/* Title */}
        <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2, marginBottom: 10 }}>
          本週精選學校標案
        </div>

        {/* Week pill */}
        {weekLabel && (
          <div style={{
            display: "inline-block", background: "#fafa50", color: "#176100",
            fontWeight: 700, borderRadius: 8, padding: "5px 16px", fontSize: 14,
          }}>{weekLabel}精選</div>
        )}
      </div>

      {/* ── Tender card ── */}
      <div style={{ padding: "10px 10px 0", flex: 1 }}>
        <div style={{
          background: "white", borderRadius: 16, border: "1px solid #e2e8f0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.07)", padding: "14px 14px 12px",
        }}>

          {/* Number badge + category + status */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%", background: "#15803d",
                color: "white", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 700, flexShrink: 0,
              }}>{idx + 1}</div>
              <div style={{
                border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#15803d",
                borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600,
              }}>🏷️ {catLabel}</div>
            </div>
            <div style={{
              background: isOpen ? "#dcfce7" : "#fee2e2",
              border: `1px solid ${isOpen ? "#86efac" : "#fca5a5"}`,
              color: isOpen ? "#15803d" : "#dc2626",
              borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700,
            }}>{isOpen ? "招標中" : "已截止"}</div>
          </div>

          {/* Title */}
          <div style={{
            fontSize: 17, fontWeight: 700, color: "#1e293b", lineHeight: 1.4,
            marginBottom: 8,
            overflow: "hidden", display: "-webkit-box",
            WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          }}>{tender.title}</div>

          {/* District + deadline */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 12px", fontSize: 12, color: "#64748b", marginBottom: 9 }}>
            <span>📍 {tender.district ?? "香港"}</span>
            <span>📅 截止：{tender.deadlineDate}
              {days > 0 && (
                <span style={{ marginLeft: 4, color: "#b45309", fontWeight: 600 }}>（餘 {days} 日）</span>
              )}
            </span>
          </div>

          {/* School info block */}
          {(tender.schoolName || tender.contactPhone) && (
            <div style={{
              background: "#fffbeb", border: "1.5px solid #fde68a",
              borderRadius: 10, padding: "9px 11px", color: "#78350f",
            }}>
              {tender.schoolName && (
                <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 14, marginBottom: 4 }}>
                  學校：{tender.schoolName}
                </div>
              )}
              {tender.contactPhone && (
                <div style={{ fontSize: 12, marginBottom: 2 }}>📞 {tender.contactPhone}</div>
              )}
              {tender.contactEmail && (
                <div style={{ fontSize: 12 }}>✉️ {tender.contactEmail}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Bottom branding bar ── */}
      <div style={{
        padding: "9px 14px 11px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{
            width: 20, height: 20, borderRadius: "50%", background: "#15803d",
            color: "white", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 9, fontWeight: 800,
          }}>S</div>
          <span style={{ fontWeight: 800, color: "#166534", fontSize: 13 }}>SKOLTEND</span>
          <span style={{ color: "#cbd5e1", fontSize: 13 }}>·</span>
          <span style={{ fontSize: 12, color: "#94a3b8" }}>全港學校招標資訊第一站</span>
        </div>
        <span style={{ fontSize: 12, color: "#94a3b8" }}>skoltend.com</span>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminMetaPage() {
  const { data, isLoading } = useGetFeaturedTenders({
    query: { queryKey: ["getFeaturedTenders"] },
  });
  const { getToken } = useAuth();
  const { toast } = useToast();
  const tenders = data?.tenders ?? [];

  const TOTAL = 6;
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [captions, setCaptions] = useState<string[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [generating, setGenerating] = useState<boolean[]>(Array(TOTAL).fill(false));
  const [generatingText, setGeneratingText] = useState<boolean[]>(Array(TOTAL).fill(false));
  const [posting, setPosting] = useState<boolean[]>(Array(TOTAL).fill(false));
  const [metaStatus, setMetaStatus] = useState<{ connected: boolean | null; instagramConfigured?: boolean; threadsConfigured?: boolean }>({ connected: null });
  // Platform toggles
  const [platforms, setPlatforms] = useState<Set<string>>(new Set(["facebook"]));
  // 6th card — custom date/time (default: next Friday 17:49)
  const [customDateTime, setCustomDateTime] = useState<string>(() => {
    const d = new Date();
    const toFri = (5 - d.getDay() + 7) % 7 || 7;
    d.setDate(d.getDate() + toFri);
    d.setHours(17, 49, 0, 0);
    return d.toISOString().slice(0, 16);
  });

  // Convenience wrappers that bind customDateTime for the 6th slot
  const scheduleTs = (idx: number) => getScheduleTimestamp(idx, idx === 5 ? customDateTime : undefined);
  const formatDate = (idx: number) => formatScheduleDate(idx, idx === 5 ? customDateTime : undefined);

  // Init captions when tenders load (6 slots; slot 5 is free-form → empty)
  const initCaptions = useCallback((ts: any[]) => {
    if (captions.length === 0 && ts.length > 0) {
      const caps = ts.map((t, i) => generateCaption(t, i));
      while (caps.length < TOTAL) caps.push("");
      setCaptions(caps);
      setImageUrls(new Array(TOTAL).fill(""));
    }
  }, [captions.length]);

  if (tenders.length > 0 && captions.length === 0) initCaptions(tenders);

  // Check Meta connection
  if (metaStatus.connected === null) {
    getToken().then(token => {
      fetch("/api/admin/meta/status", { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => setMetaStatus({ connected: d.connected, instagramConfigured: d.instagramConfigured, threadsConfigured: d.threadsConfigured }))
        .catch(() => setMetaStatus({ connected: false }));
    });
  }
  const metaConnected = metaStatus.connected;

  async function generateImage(idx: number) {
    const el = cardRefs.current[idx];
    if (!el) return;
    setGenerating(prev => { const n = [...prev]; n[idx] = true; return n; });
    try {
      // Capture at 2× pixel ratio → 780×780 from the 390×390 wrapper
      const url = await toPng(el, { pixelRatio: 2 });
      setImageUrls(prev => { const n = [...prev]; n[idx] = url; return n; });
    } catch (e: any) {
      toast({ title: "❌ 生成圖片失敗", description: e.message, variant: "destructive" });
    } finally {
      setGenerating(prev => { const n = [...prev]; n[idx] = false; return n; });
    }
  }

  function downloadImage(idx: number) {
    const url = imageUrls[idx];
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `skoltend-day${idx + 1}-${WEEKDAYS[idx]}.png`;
    a.click();
  }

  async function postToMeta(idx: number, schedule?: boolean) {
    let url = imageUrls[idx];
    if (!url && idx < 5) { await generateImage(idx); url = imageUrls[idx]; }
    if (!url) { toast({ title: "請先上載或生成圖片", variant: "destructive" }); return; }

    setPosting(prev => { const n = [...prev]; n[idx] = true; return n; });
    try {
      const token = await getToken();
      const body: any = {
        imageBase64: url,
        caption: captions[idx],
        platforms: [...platforms],
      };
      if (schedule) body.scheduleTimestamp = scheduleTs(idx);

      const res = await fetch("/api/admin/meta/post", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok && !d.ok) throw new Error(d.error ?? "發佈失敗");

      // Show per-platform results
      const successParts = Object.keys(d.results ?? {}).join("、") || "未知";
      const errParts = d.errors ? Object.entries(d.errors as Record<string, string>).map(([k, v]) => `${k}: ${v}`).join("\n") : null;
      toast({
        title: schedule ? `✅ 已排程 ${WEEKDAYS[idx]}（${successParts}）` : `✅ 已即時發佈（${successParts}）`,
        description: [schedule ? `時間：${formatDate(idx)}` : "", errParts ? `⚠️ 部分失敗：${errParts}` : ""].filter(Boolean).join("\n") || undefined,
      });
    } catch (e: any) {
      toast({ title: "❌ 發佈失敗", description: e.message, variant: "destructive" });
    } finally {
      setPosting(prev => { const n = [...prev]; n[idx] = false; return n; });
    }
  }

  function uploadImage(idx: number) {
    const input = fileInputRefs.current[idx];
    if (!input) return;
    input.value = "";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = e => {
        const url = e.target?.result as string;
        setImageUrls(prev => { const n = [...prev]; n[idx] = url; return n; });
      };
      reader.readAsDataURL(file);
    };
    input.click();
  }

  async function generateAICaption(idx: number) {
    const tender = tenders[idx];
    if (!tender) return; // 6th card (idx 5) has no tender — user types manually
    setGeneratingText(prev => { const n = [...prev]; n[idx] = true; return n; });
    try {
      const token = await getToken();
      const publishTs = scheduleTs(idx) * 1000;
      const publishDateIso = new Date(publishTs).toISOString().split("T")[0];
      const res = await fetch("/api/admin/meta/generate-caption", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tender, publishDateIso }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error ?? "AI 生成失敗");
      setCaptions(prev => { const n = [...prev]; n[idx] = d.caption; return n; });
    } catch (e: any) {
      toast({ title: "❌ AI 生成失敗", description: e.message, variant: "destructive" });
    } finally {
      setGeneratingText(prev => { const n = [...prev]; n[idx] = false; return n; });
    }
  }

  async function scheduleAllWeek() {
    const count = Math.min(tenders.length, TOTAL);
    for (let i = 0; i < count; i++) {
      await postToMeta(i, true);
    }
    toast({ title: `✅ 本週 ${count} 日貼文全部排程完成` });
  }

  return (
    <AdminLayout tab="meta">
      {/* Connection status */}
      <div className="mb-4">
        <Card className={metaConnected ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}>
          <CardContent className="p-4 flex items-start gap-3">
            {metaConnected === null ? (
              <Loader2 className="h-5 w-5 animate-spin mt-0.5 text-muted-foreground" />
            ) : metaConnected ? (
              <CheckCircle2 className="h-5 w-5 mt-0.5 text-green-600" />
            ) : (
              <AlertCircle className="h-5 w-5 mt-0.5 text-amber-600" />
            )}
            <div className="flex-1 min-w-0">
              {metaConnected === null && <p className="text-sm text-muted-foreground">正在確認 Meta 連線狀態…</p>}
              {metaConnected === true && <p className="text-sm text-green-700 font-medium">✅ Meta 已連接，可直接排程發佈</p>}
              {metaConnected === false && (
                <div>
                  <p className="text-sm font-semibold text-amber-700 mb-2">⚠️ Meta 尚未連接 — 現可預覽及下載圖片，排程需先設定憑證</p>
                  <details className="text-xs text-amber-700 space-y-1">
                    <summary className="cursor-pointer font-medium">查看 Facebook 設定步驟</summary>
                    <div className="mt-2 space-y-1 pl-2 border-l-2 border-amber-300">
                      <p>1. 前往 <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="underline">developers.facebook.com</a> 建立 App</p>
                      <p>2. 加入「Pages API」權限 (<code>pages_manage_posts</code>, <code>pages_read_engagement</code>)</p>
                      <p>3. 在 Graph API Explorer 取得長期 Page Access Token</p>
                      <p>4. 將 <code>META_PAGE_ID</code> 及 <code>META_PAGE_ACCESS_TOKEN</code> 加入 Replit Secrets</p>
                    </div>
                  </details>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Platform toggles + IG/Threads setup guide */}
      <div className="mb-6">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-muted-foreground mb-3">選擇發佈平台</p>
            <div className="flex flex-wrap gap-2">
              {/* Facebook — always enabled */}
              <Button size="sm" variant={platforms.has("facebook") ? "default" : "outline"}
                className="gap-1.5 text-xs"
                onClick={() => setPlatforms(prev => { const n = new Set(prev); if (n.has("facebook") && n.size > 1) n.delete("facebook"); else n.add("facebook"); return n; })}>
                <span>🔵</span> Facebook
              </Button>

              {/* Instagram */}
              {metaStatus.instagramConfigured ? (
                <Button size="sm" variant={platforms.has("instagram") ? "default" : "outline"}
                  className="gap-1.5 text-xs"
                  onClick={() => setPlatforms(prev => { const n = new Set(prev); if (n.has("instagram") && n.size > 1) n.delete("instagram"); else n.add("instagram"); return n; })}>
                  <span>📸</span> Instagram
                </Button>
              ) : (
                <details className="inline">
                  <summary className="cursor-pointer">
                    <Button size="sm" variant="outline" className="gap-1.5 text-xs opacity-50 pointer-events-none" asChild>
                      <span><span>📸</span> Instagram（未設定）</span>
                    </Button>
                  </summary>
                  <div className="mt-2 p-3 bg-muted rounded-lg text-xs space-y-1 text-muted-foreground border">
                    <p className="font-semibold text-foreground">如何設定 Instagram 發佈：</p>
                    <p>1. 在 Meta Business Suite 將 Instagram Business 帳號連結到你的 Facebook 專頁</p>
                    <p>2. 在 <a href="https://graph.facebook.com/v19.0/me?fields=instagram_business_account&access_token=YOUR_TOKEN" target="_blank" rel="noopener noreferrer" className="underline text-blue-600">Graph API</a> 查詢 instagram_business_account.id</p>
                    <p>3. Token 需新增權限：<code>instagram_basic</code>、<code>instagram_content_publish</code></p>
                    <p>4. 將 Instagram Business Account ID 加入 Replit Secret：<code>META_INSTAGRAM_USER_ID</code></p>
                  </div>
                </details>
              )}

              {/* Threads */}
              {metaStatus.threadsConfigured ? (
                <Button size="sm" variant={platforms.has("threads") ? "default" : "outline"}
                  className="gap-1.5 text-xs"
                  onClick={() => setPlatforms(prev => { const n = new Set(prev); if (n.has("threads") && n.size > 1) n.delete("threads"); else n.add("threads"); return n; })}>
                  <span>🔘</span> Threads
                </Button>
              ) : (
                <details className="inline">
                  <summary className="cursor-pointer">
                    <Button size="sm" variant="outline" className="gap-1.5 text-xs opacity-50 pointer-events-none" asChild>
                      <span><span>🔘</span> Threads（未設定）</span>
                    </Button>
                  </summary>
                  <div className="mt-2 p-3 bg-muted rounded-lg text-xs space-y-1 text-muted-foreground border">
                    <p className="font-semibold text-foreground">如何設定 Threads 發佈：</p>
                    <p>1. 前往 <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="underline text-blue-600">Meta for Developers</a>，在 App 加入 Threads API 產品</p>
                    <p>2. 申請 <code>threads_content_publish</code>、<code>threads_manage_insights</code> 權限</p>
                    <p>3. 用 <code>/me?fields=id&access_token=TOKEN</code> 查詢 Threads User ID（需用 Threads 的 access token）</p>
                    <p>4. 加入 Replit Secrets：<code>META_THREADS_USER_ID</code>（Threads User ID）</p>
                    <p>5. 如 token 不同，亦加入 <code>META_THREADS_ACCESS_TOKEN</code></p>
                    <p className="text-amber-600">⚠️ Threads API 目前不支援排程發佈，只可即時發佈</p>
                  </div>
                </details>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hidden render area — captured by html-to-image as 390×390 square */}
      <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none" }}>
        {tenders.map((tender, idx) => (
          <div
            key={tender.id}
            ref={el => { cardRefs.current[idx] = el; }}
            style={{ width: 390, height: 390, overflow: "hidden" }}
          >
            <WeeklyExampleMobileCapture tender={tender} weekKey={data?.weekKey ?? ""} idx={idx} />
          </div>
        ))}
      </div>

      {/* Schedule rows */}
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold">本週精選貼文（{data?.weekKey ?? "…"}）</h2>
          {metaConnected && (
            <Button onClick={scheduleAllWeek} className="gap-1.5" disabled={isLoading}>
              <Calendar className="h-4 w-4" /> 排程本週全部
            </Button>
          )}
        </div>

        {isLoading && Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-48 rounded-xl" />
        ))}

        {/* Hidden file inputs — one per slot (6 total) */}
        {Array.from({ length: TOTAL }).map((_, idx) => (
          <input key={idx} type="file" accept="image/*" className="hidden"
            ref={el => { fileInputRefs.current[idx] = el; }} />
        ))}

        {!isLoading && (tenders as any[]).map((tender, idx) => (
          <Card key={tender.id} className="overflow-hidden">
            <CardHeader className="py-3 px-5 bg-secondary/30 border-b flex-row items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold shrink-0">{idx + 1}</div>
              <div>
                <span className="font-semibold text-sm">{WEEKDAYS[idx]}</span>
                <span className="text-xs text-muted-foreground ml-2">{formatDate(idx)}</span>
              </div>
              <Badge variant="outline" className="ml-auto text-xs">{tender.categories?.[0]?.nameChinese ?? "其他"}</Badge>
            </CardHeader>

            <CardContent className="p-4">
              <div className="flex gap-4">
                {/* Preview thumbnail */}
                <div className="shrink-0">
                  {imageUrls[idx] ? (
                    <img src={imageUrls[idx]} alt="" className="w-[135px] h-[135px] rounded-lg object-cover border" />
                  ) : (
                    <div className="w-[135px] h-[135px] rounded-lg border bg-secondary/40 flex flex-col items-center justify-center gap-1 text-muted-foreground">
                      <ImageIcon className="h-8 w-8 opacity-30" />
                      <span className="text-[10px] text-center px-2">點「生成圖片」預覽</span>
                    </div>
                  )}
                  <div className="mt-1.5 space-y-1 w-[135px]">
                    <Button size="sm" variant="outline" className="w-full text-xs h-7 gap-1"
                      onClick={() => generateImage(idx)} disabled={generating[idx]}>
                      {generating[idx] ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />}
                      {generating[idx] ? "生成中…" : "生成圖片"}
                    </Button>
                    <Button size="sm" variant="outline" className="w-full text-xs h-7 gap-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => uploadImage(idx)}>
                      <Upload className="h-3 w-3" /> 上載圖片
                    </Button>
                    {imageUrls[idx] && (
                      <Button size="sm" variant="outline" className="w-full text-xs h-7 gap-1" onClick={() => downloadImage(idx)}>
                        <Download className="h-3 w-3" /> 下載 PNG
                      </Button>
                    )}
                  </div>
                </div>

                {/* Caption editor */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs font-medium text-muted-foreground truncate">
                      📌 {tender.title}
                    </div>
                    <Button size="sm" variant="outline"
                      className="text-xs h-6 gap-1 text-violet-600 border-violet-200 hover:bg-violet-50 shrink-0 ml-2"
                      onClick={() => generateAICaption(idx)} disabled={generatingText[idx]}>
                      {generatingText[idx]
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <Sparkles className="h-3 w-3" />}
                      {generatingText[idx] ? "生成中…" : "AI 生成文字"}
                    </Button>
                  </div>
                  <Textarea
                    className="text-xs h-[120px] resize-none font-mono"
                    value={captions[idx] ?? ""}
                    onChange={e => setCaptions(prev => { const n = [...prev]; n[idx] = e.target.value; return n; })}
                  />
                  <div className="flex gap-1.5 mt-2">
                    {metaConnected && (
                      <>
                        <Button size="sm" variant="outline" className="text-xs gap-1 text-blue-600 border-blue-200"
                          onClick={() => postToMeta(idx, false)} disabled={posting[idx]}>
                          {posting[idx] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                          即時發佈
                        </Button>
                        <Button size="sm" className="text-xs gap-1"
                          onClick={() => postToMeta(idx, true)} disabled={posting[idx]}>
                          {posting[idx] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Calendar className="h-3 w-3" />}
                          排程 {WEEKDAYS[idx]}
                        </Button>
                      </>
                    )}
                    {!metaConnected && imageUrls[idx] && (
                      <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => downloadImage(idx)}>
                        <Download className="h-3 w-3" /> 下載圖片
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* ── 6th card: free-form post with custom date/time ─────────────── */}
        {!isLoading && (
          <Card className="overflow-hidden border-dashed border-2 border-primary/30">
            <CardHeader className="py-3 px-5 bg-primary/5 border-b flex-row items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-primary/70 text-white flex items-center justify-center text-xs font-bold shrink-0">6</div>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="font-semibold text-sm text-primary shrink-0">自訂貼文</span>
                <input
                  type="datetime-local"
                  className="text-xs border rounded px-2 py-0.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  value={customDateTime}
                  onChange={e => setCustomDateTime(e.target.value)}
                />
              </div>
              <Badge variant="outline" className="ml-auto text-xs text-primary border-primary/40 shrink-0">自由排程</Badge>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex gap-4">
                {/* Image area — upload only (no tender data for auto-generate) */}
                <div className="shrink-0">
                  {imageUrls[5] ? (
                    <img src={imageUrls[5]} alt="" className="w-[135px] h-[135px] rounded-lg object-cover border" />
                  ) : (
                    <div className="w-[135px] h-[135px] rounded-lg border bg-secondary/40 flex flex-col items-center justify-center gap-1 text-muted-foreground">
                      <ImageIcon className="h-8 w-8 opacity-30" />
                      <span className="text-[10px] text-center px-2">上載自訂圖片</span>
                    </div>
                  )}
                  <div className="mt-1.5 space-y-1 w-[135px]">
                    <Button size="sm" variant="outline" className="w-full text-xs h-7 gap-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => uploadImage(5)}>
                      <Upload className="h-3 w-3" /> 上載圖片
                    </Button>
                    {imageUrls[5] && (
                      <Button size="sm" variant="outline" className="w-full text-xs h-7 gap-1" onClick={() => downloadImage(5)}>
                        <Download className="h-3 w-3" /> 下載 PNG
                      </Button>
                    )}
                  </div>
                </div>

                {/* Caption editor */}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-muted-foreground mb-1">✏️ 自由輸入貼文內容</div>
                  <Textarea
                    className="text-xs h-[120px] resize-none font-mono"
                    placeholder="在此輸入貼文文字…"
                    value={captions[5] ?? ""}
                    onChange={e => setCaptions(prev => { const n = [...prev]; n[5] = e.target.value; return n; })}
                  />
                  <div className="flex gap-1.5 mt-2">
                    {metaConnected && (
                      <>
                        <Button size="sm" variant="outline" className="text-xs gap-1 text-blue-600 border-blue-200"
                          onClick={() => postToMeta(5, false)} disabled={posting[5]}>
                          {posting[5] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                          即時發佈
                        </Button>
                        <Button size="sm" className="text-xs gap-1"
                          onClick={() => postToMeta(5, true)} disabled={posting[5]}>
                          {posting[5] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Calendar className="h-3 w-3" />}
                          排程 {customDateTime ? new Date(customDateTime).toLocaleString("zh-HK", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "自訂時間"}
                        </Button>
                      </>
                    )}
                    {!metaConnected && imageUrls[5] && (
                      <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => downloadImage(5)}>
                        <Download className="h-3 w-3" /> 下載圖片
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
